/* @flow */

import React, { createContext, useContext, useState } from "react";
import useCustomer from "helpers/use-customer";
import { StoreInfoContext } from "entrypoint/shared";
import { useData } from "crustate/react";
import { CmsBlockData } from "data";

type Notifications = {
  showNotificationBar: boolean,
  showAddressNotificationBar: boolean,
  showMiniBanner: boolean,
  headerNotificationsActive: number,
  notificationsActive: number,
  setShowNotificationBar: (value: boolean) => void,
  setShowAddressNotificationBar: (value: boolean) => void,
  heightToUse: () => string,
};

const NotificationContext = createContext<Notifications>({});

export const NotificationProvider = ({ children }: { children: React$Node }) => {
  const { info } = useContext(StoreInfoContext);
  const { missingAddress } = useCustomer();
  const miniBannerData = useData(CmsBlockData);

  const [showNotificationBar, setShowNotificationBar] = useState(Boolean(info.popUp?.body));
  const [showAddressNotificationBar, setShowAddressNotificationBar] = useState(missingAddress);
  const showMiniBanner = miniBannerData.state === "LOADED" && miniBannerData.data !== null;

  const notifications = [
    showNotificationBar,
    showAddressNotificationBar,
    showMiniBanner,
  ];

  const headerBaseNotifications = [
    showAddressNotificationBar,
    showMiniBanner,
  ];

  const notificationsActive = notifications.filter(Boolean).length;
  const headerNotificationsActive = headerBaseNotifications.filter(Boolean).length;

  const heightToUse = () => {
    switch (headerNotificationsActive) {
      case 0:
        if (showNotificationBar) {
          return "banner";
        }

        return "none";
      case 1:
        if (showAddressNotificationBar) {
          return "banner";
        }

        return "miniBanner";
      case 2:
        return "multiple";

      default:
        return "none";
    }
  };

  const value = {
    showNotificationBar,
    showAddressNotificationBar,
    showMiniBanner,
    headerNotificationsActive,
    notificationsActive,
    setShowNotificationBar,
    setShowAddressNotificationBar,
    heightToUse,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotification = (): Notifications => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error("useNotification() must be used within NotificationProvider");
  }

  return context;
};
